import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RefreshCw, Play, Pause, Crosshair, Coffee, TriangleAlert, LocateFixed, Route } from "lucide-react";
import { api, getSavedPos, pickActive } from "@/lib/api";
import { useNavigation } from "@/hooks/useNavigation";
import { NavMap } from "@/components/NavMap";
import { NavInstruction } from "@/components/NavInstruction";
import { AlertsPanel } from "@/components/AlertsPanel";
import { BigButton } from "@/components/BigButton";
import { TachoWidget } from "@/components/TachoWidget";
import { t } from "@/i18n";

const Ctl = ({ icon: I, label, onClick, active, testId, color = "#4accff", disabled }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled}
    className="nv-press min-h-[72px] rounded-2xl flex flex-col items-center justify-center gap-1 font-display uppercase font-bold text-sm sm:text-base border-2 disabled:opacity-40"
    style={{ background: active ? (color === "#ffffff" ? "var(--nv-fg)" : color) : "var(--nv-overlay)", borderColor: color === "#ffffff" ? "var(--nv-fg)" : color, color: active ? (color === "#ffffff" ? "var(--nv-bg)" : "#0b0f17") : "var(--nv-fg)" }}>
    <I className={`h-7 w-7 ${disabled ? "" : ""}`} /> {label}
  </button>
);

export default function Navigator() {
  const [sp] = useSearchParams();
  const navTo = useNavigate();
  const [trip, setTrip] = useState(undefined);
  const [follow, setFollow] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);

  useEffect(() => {
    const id = sp.get("trip");
    if (id) api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch(() => setTrip(null));
    else api.get("/trips").then((r) => setTrip(pickActive(r.data))).catch(() => setTrip(null));
  }, [sp]);

  const dest = useMemo(() => {
    if (sp.get("lat")) return { lat: +sp.get("lat"), lng: +sp.get("lng"), name: sp.get("name") || t("Destinazione") };
    if (!trip) return null;
    return ["assegnato", "accettato"].includes(trip.status) ? trip.pickup : trip.delivery;
  }, [sp, trip]);

  const n = useNavigation({ trip, dest });
  const { pos, setPos, calc } = n;

  useEffect(() => {
    if (trip === undefined || pos) return;
    const lp = trip?.last_position;
    const start = getSavedPos() || (lp ? [lp.lat, lp.lng] : trip ? [trip.pickup.lat - 0.25, trip.pickup.lng - 0.2] : null);
    if (start) setPos(start);
  }, [trip, pos, setPos]);

  useEffect(() => {
    if (pos && dest && !n.route && !n.busy) calc(pos, t("Percorso calcolato"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, dest]);

  if (trip === undefined) return <div className="p-6 text-slate-400">{t("Caricamento…")}</div>;
  if (!dest) return (
    <div className="max-w-xl mx-auto p-6 space-y-4" data-testid="nav-no-trip">
      <h1 className="font-display text-4xl font-extrabold uppercase">{t("Nessuna destinazione")}</h1>
      <p className="text-slate-400">{t("Accetta un viaggio per avviare il navigatore.")}</p>
      <BigButton testId="nav-go-trips" icon={Route} label={t("Vai ai viaggi")} onClick={() => navTo("/")} />
    </div>
  );

  const r = n.route;
  return (
    <div className="fixed inset-x-0 bottom-[84px] top-[var(--nv-header,72px)]" data-testid="navigator-page">
      <NavMap pos={pos} route={r} passedIdx={n.idx} dest={dest} alerts={n.alerts} follow={follow} />
      <div className="absolute top-3 inset-x-3 z-[400] lg:right-[420px] space-y-2">
        {r && <NavInstruction {...n.nav} />}
        {r && (
          <div className="flex flex-nowrap sm:flex-wrap gap-2 text-sm overflow-x-auto whitespace-nowrap pb-1" data-testid="route-meta">
            <span className="rounded-full bg-[#0b0f17]/90 px-3 py-1 border border-[#243047]">{t("Verso:")} <b>{dest.name}</b></span>
            <span className="rounded-full bg-[#0b0f17]/90 px-3 py-1 border border-[#243047]">{t("Mezzo:")} <b>{t(trip?.vehicle?.type || "standard")} {trip?.vehicle?.weight_t ? `${trip.vehicle.weight_t} t` : ""}</b></span>
            <span className="rounded-full bg-[#0b0f17]/90 px-3 py-1 border border-[#243047]" data-testid="route-provider">{t("Mappa:")} {r.provider}</span>
            {r.rerouted && <span className="rounded-full bg-[#10b981] text-[#0b0f17] px-3 py-1 font-bold" data-testid="route-rerouted">{t("Percorso alternativo")} · {r.delay_min >= 0 ? "+" : ""}{r.delay_min} min</span>}
            {r.on_route.length > 0 && <span className="rounded-full bg-[#ef0298] px-3 py-1 font-bold" data-testid="route-onroute-alerts">{t("{n} avvisi sul percorso", { n: r.on_route.length })}</span>}
          </div>
        )}
        {n.busy && <div className="rounded-xl bg-[#ef0298] px-4 py-3 font-display text-xl font-bold uppercase flex items-center gap-2" data-testid="route-recalculating"><RefreshCw className="h-6 w-6 animate-spin" /> {t("Ricalcolo percorso…")}</div>}
      </div>
      <aside className="hidden lg:flex flex-col gap-3 absolute top-3 right-3 bottom-3 w-[400px] z-[400] overflow-y-auto">
        <TachoWidget compact />
        <AlertsPanel alerts={n.alerts} route={r} />
      </aside>
      <div className="absolute bottom-3 inset-x-3 lg:right-[420px] z-[400] grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="nav-controls">
        <Ctl testId="nav-recalc-btn" icon={RefreshCw} label={t("Ricalcola")} color="#ef0298" active onClick={() => calc(pos, t("Percorso aggiornato"))} disabled={n.busy} />
        <Ctl testId="nav-sim-btn" icon={n.sim ? Pause : Play} label={t(n.sim ? "Ferma sim." : "Simula guida")} active={n.sim} onClick={() => n.setSim(!n.sim)} disabled={!r || n.gps} />
        <Ctl testId="nav-gps-btn" icon={LocateFixed} label={t(n.gps ? "GPS attivo" : "Usa GPS")} active={n.gps} onClick={() => { n.setSim(false); n.setGps(!n.gps); }} />
        <Ctl testId="nav-follow-btn" icon={Crosshair} label={t(follow ? "Segui: sì" : "Segui mezzo")} active={follow} onClick={() => setFollow(!follow)} color="#ffffff" />
        <Ctl testId="nav-sim-alert-btn" icon={TriangleAlert} label={t("Avviso test")} onClick={n.simulateAlert} color="#f59e0b" disabled={!r} />
        <Ctl testId="nav-break-btn" icon={Coffee} label={t("Pausa")} onClick={() => navTo("/pausa")} color="#10b981" />
      </div>
      <button data-testid="nav-mobile-alerts-btn" onClick={() => setShowAlerts(!showAlerts)}
        className="lg:hidden nv-press absolute top-[180px] right-3 z-[410] min-h-[56px] px-4 rounded-xl bg-[#ef0298] font-display font-bold uppercase">
        {showAlerts ? t("Chiudi") : t("Avvisi e notizie ({n})", { n: n.alerts.length })}
      </button>
      {showAlerts && (
        <div className="lg:hidden absolute top-[244px] inset-x-3 bottom-[100px] z-[405] overflow-y-auto space-y-3" data-testid="nav-mobile-alerts">
          <TachoWidget compact />
          <AlertsPanel alerts={n.alerts} route={r} />
        </div>
      )}
    </div>
  );
}
